import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle, RotateCcw } from 'lucide-react';
import { ToastContainer } from "react-toastify";
import Loader from "../../components/Loader/Loader";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import { ServiceUsageService, fetchVendorServiceUsage } from '../../services/customerService';
import Chart from 'chart.js/auto';
import ReportHeading from '../Components/ReportHeadings/ReportHeadings';

const ServiceUsageReport = () => {
  const getServerErrorMessage = (error, fallback = 'Failed to fetch service usage data') => {
    const errorData = error?.response?.data || error?.data || {};

    if (errorData?.errors && typeof errorData.errors === 'object') {
      const firstFieldErrors = Object.values(errorData.errors)[0];
      if (Array.isArray(firstFieldErrors) && firstFieldErrors[0]) {
        return firstFieldErrors[0];
      }
      if (typeof firstFieldErrors === 'string') {
        return firstFieldErrors;
      }
    }

    return errorData?.status?.message || errorData?.message || error?.message || fallback;
  };

  const getResponseErrorMessage = (response, fallback = 'Failed to fetch service usage data') => {
    if (response?.errors && typeof response.errors === 'object') {
      const firstFieldErrors = Object.values(response.errors)[0];
      if (Array.isArray(firstFieldErrors) && firstFieldErrors[0]) {
        return firstFieldErrors[0];
      }
      if (typeof firstFieldErrors === 'string') {
        return firstFieldErrors;
      }
    }

    return response?.status?.message || response?.message || fallback;
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const initialFilters = {
    from_date: getTodayDate(),
    to_date: getTodayDate(),
    vendor_id: '',
    service_id: '',
    per_page: 50,
  };

  const [filters, setFilters] = useState(initialFilters);
  const [reportData, setReportData] = useState([]);
  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [summaryData, setSummaryData] = useState(null);
  
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const isInitialMount = useRef(true);

  // Client-side filtering by search term - moved before useEffect that uses it
  const filteredData = useMemo(() => {
    const safeReportData = Array.isArray(reportData) ? reportData : [];
    if (!debouncedSearch) return safeReportData;
    
    return safeReportData.filter(item => 
      item.service_name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      item.service_code?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      item.end_point?.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [reportData, debouncedSearch]);

  // Load services and initial data on mount
  useEffect(() => {
    loadServices();
    handleSearch(1);
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Auto-fetch for non-date filters (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    setCurrentPage(1);
    handleSearch(1);
  }, [filters.vendor_id, filters.service_id, filters.per_page]);

  // Fetch on page change
  useEffect(() => {
    if (currentPage > 1) {
      handleSearch(currentPage);
    }
  }, [currentPage]);

  // Update chart when filtered data changes
  useEffect(() => {
    if (filteredData.length > 0) {
      updateChart();
    }
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [filteredData]);

  const loadServices = async () => {
    try {
      const response = await fetchVendorServiceUsage({ 
        vendor_id: filters.vendor_id || undefined,
        per_page: 200 
      });
      
      if (response?.status?.code === 200 && response?.data) {
        setServices(Array.isArray(response.data) ? response.data : response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load services:', error);
      showErrorToast('Failed to load services');
    }
  };

  const handleSearch = async (page = 1) => {
    try {
      setLoading(true);
      
      const params = Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => value && key !== 'per_page')
      );
      params.per_page = filters.per_page;

      const response = await ServiceUsageService(params, page);
      
      if (response?.status?.code === 200 && Array.isArray(response?.data)) {
        setReportData(response.data);
        setSummaryData(response);
        
        if (response.pagination) {
          setTotalPages(response.pagination.last_page || 1);
          setTotalRecords(response.pagination.total || 0);
          setCurrentPage(response.pagination.current_page || 1);
        }
        
       // showSuccessToast(`Loaded ${response.data?.length || 0} records`);
      } else {
        showErrorToast(getResponseErrorMessage(response, 'Failed to fetch service usage data'));
        setReportData([]);
      }
    } catch (error) {
      showErrorToast(getServerErrorMessage(error, 'Failed to fetch service usage data'));
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

    const handleApplyFilters = () => {
    setCurrentPage(1);
    handleSearch(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleReset = () => {
    setFilters({
      from_date: '',
      to_date: '',
      vendor_id: '',
      service_id: '',
      per_page: 50,
    });
    setSearchTerm('');
    setCurrentPage(1);
    isInitialMount.current = false;
    
    setTimeout(() => {
      handleSearch(1);
    }, 0);
  };

  const calculateSuccessRate = (successCount, failedCount, cacheCount) => {
    const successNum = parseInt(successCount) || 0;
    const failedNum = parseInt(failedCount) || 0;
    const cacheNum = parseInt(cacheCount) || 0;
    const total = successNum + failedNum + cacheNum;
    
    if (total === 0) return '0.0';
    const rate = ((successNum + cacheNum) / total) * 100;
    return rate.toFixed(1);
  };

  const updateChart = () => {
    if (!chartRef.current) return;

    const totals = filteredData.reduce((acc, item) => ({
      success: acc.success + (parseInt(item.success_responses) || 0),
      cache: acc.cache + (parseInt(item.cache_responses) || 0),
      failed: acc.failed + (parseInt(item.failed_responses) || 0),
    }), { success: 0, cache: 0, failed: 0 });

    // Cleanup previous chart instance
    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    chartInstance.current = new Chart(chartRef.current, {
      type: 'bar',
      data: {
        labels: ['Success', 'Cache', 'Failed'],
        datasets: [{
          label: 'API Requests',
          data: [totals.success, totals.cache, totals.failed],
          backgroundColor: ['#10b981', '#3b82f6', '#ef4444'],
          borderRadius: 8,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: function(context) {
                return context.label + ': ' + context.parsed.y.toLocaleString();
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return value.toLocaleString();
              }
            }
          }
        }
      }
    });
  };


  const dataWithRate = filteredData.map(item => ({
  ...item,
  successRate: calculateSuccessRate(
    item.success_responses,
    item.failed_responses,
    item.cache_responses
  )
}));

const topSuccessServices = [...dataWithRate]
  .sort((a, b) => b.success_percentage - a.success_percentage)
  .slice(0, 7);

const lowSuccessServices = [...dataWithRate]
  .sort((a, b) => a.success_percentage - b.success_percentage)
  .slice(0, 7);



  return (
    <motion.div
      initial={{ opacity: 0, y: -30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="p-1 md:p-1 w-full mx-auto"
    >
      {loading && <Loader />}
      <ToastContainer />
      
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-1 md:p-1 md:mt-4 mt-2">
        <ReportHeading name="Service Usage Report" />

        {/* Filters */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-2 rounded-xl shadow-sm mb-2 border border-blue-100">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">From Date</label>
              <input
                type="date"
                name="from_date"
                value={filters.from_date}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-lg P-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">To Date</label>
              <input
                type="date"
                name="to_date"
                value={filters.to_date}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-lg p-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">Select Service</label>
              <select
                name="service_id"
                value={filters.service_id}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-lg p-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
              >
                <option value="">All Services</option>
                {services.map(service => (
                  <option key={service.id} value={service.id}>
                    {service.service_name} ({service.service_code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">Per Page</label>
              <select
                name="per_page"
                value={filters.per_page}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-lg p-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
               {/* Reset Button */}
        
         <div className="mt-4 flex items-center gap-2">
                    <button
                      onClick={handleApplyFilters}
                      disabled={loading}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-medium p-1 rounded disabled:bg-blue-400 transition-colors"
                    >
                      Apply Filters
                    </button>
                    <button
                      onClick={handleReset}
                      disabled={loading}
                      className="bg-gray-600 hover:bg-gray-700 text-white font-medium p-1 rounded disabled:bg-gray-400 transition-colors flex items-center gap-2"
                    >
                      <RotateCcw size={18} />
                      Reset Filters
                    </button>
                  </div>
          </div>

       
        </div>

        {/* Chart */}
       




<div className="grid grid-cols-1 md:grid-cols-3 gap-1 mb-1">

   {filteredData.length > 0 && (
          <div className="w-full bg-white p-0.5 rounded-xl shadow-lg mb-1 border border-gray-100">
            <h6 className="text-lg font-bold mb- text-gray-800">Success vs Failure Summary</h6>
            <div style={{ height: '220px' }}>
              <canvas ref={chartRef}></canvas>
            </div>
          </div>
        )}

<div className='w-full'>
  {/* Top Success */}
  <div className="bg-white rounded-lg shadow p-2 border">
    <h3 className="text-sm font-semibold text-green-700 mb-3">
       Top 5 Services by Success Rate
    </h3>

    {topSuccessServices.map((item, i) => (
      <div key={i} className="flex justify-between text-sm py-1 border-b last:border-0">
        <span className="text-gray-700">{item.service_name}</span>
        <span className="font-semibold text-green-600">{item.success_percentage}%</span>
      </div>
    ))}
  </div>
  </div>

<div className='w-full'>
   {/* Low Success */}
  <div className="bg-white rounded-lg shadow p-2 border">
    <h3 className="text-sm font-semibold text-red-700 mb-3">
       Top 5 Lowest Success Rate Services
    </h3>

    {lowSuccessServices.map((item, i) => (
      <div key={i} className="flex justify-between text-sm py-1 border-b last:border-0">
        <span className="text-gray-700">{item.service_name}</span>
        <span className="font-semibold text-red-600">{item.success_percentage}%</span>
      </div>
    ))}
  </div>
  </div>

</div>








        {/* Table Section */}
        <div className="bg-white p-1 rounded-xl shadow-lg border border-gray-100">
          <div className="flex flex-col md:flex-row justify-between items-center mb-1 gap-2">
            <h6 className="text-lg font-bold text-gray-800">
              Detailed Report 
              <span className="ml-2 text-sm font-normal text-gray-600">
                ({totalRecords.toLocaleString()} records)
              </span>
            </h6>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search Service / Code / Endpoint..."
              className="border border-gray-300 rounded-lg px-4 py-2 w-full md:w-96 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            />
          </div>

          {/* Summary Section */}
          {summaryData?.summary_totals && (
            <div className="mb-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-2">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Summary Statistics
              </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">

  {[
    { value: summaryData.summary_totals.total_api_requests.toLocaleString(), label: "Total API Requests", color: "text-blue-600" },
    { value: summaryData.summary_totals.total_cache_requests.toLocaleString(), label: "Cache Requests", color: "text-green-600" },
    { value: summaryData.summary_totals.total_vendor_requests.toLocaleString(), label: "Vendor Requests", color: "text-purple-600" },
    { value: summaryData.summary_totals.total_success_requests.toLocaleString(), label: "Success Requests", color: "text-emerald-600" },
    { value: summaryData.summary_totals.total_failed_requests.toLocaleString(), label: "Failed Requests", color: "text-red-600" },
    { value: `₹${parseFloat(summaryData.summary_totals.total_amount).toFixed(2)}`, label: "Total Amount", color: "text-orange-600" },
    { value: `₹${parseFloat(summaryData.summary_totals.total_gst).toFixed(2)}`, label: "Total GST", color: "text-teal-600" },
    // { value: `₹${parseFloat(summaryData.summary_totals.total_vendor_per_request_charge).toFixed(2)}`, label: "Per Request Charge", color: "text-indigo-600" }
  ].map((item, index) => (
    <div
      key={index}
      className="text-center p-3 rounded-xl bg-white shadow-sm 
                 transition-all duration-300 ease-in-out 
                 hover:scale-110 hover:shadow-xl hover:bg-gray-50 cursor-pointer"
    >
      <div className={`text-2xl font-bold ${item.color}`}>
        {item.value}
      </div>
      <div className="text-xs text-gray-600">{item.label}</div>
    </div>
  ))}

</div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">SRN</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Service Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Vendor Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Endpoint</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Total Requests</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Success</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Cache</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Failed</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Vendor Requests</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Success %</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Amount (₹)</th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200 border border-gray-400">
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center py-12">
                      <AlertCircle className="mx-auto mb-3 text-gray-400" size={48} />
                      <p className="text-gray-500 text-lg font-medium">No data available</p>
                      <p className="text-gray-400 text-sm mt-1">Try adjusting your filters or search term</p>
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item, index) => {
                    const successRate = calculateSuccessRate(
                      item.success_responses,
                      item.failed_responses,
                      item.cache_responses
                    );
                    const srn = (currentPage - 1) * filters.per_page + index + 1;
                    
                    return (
                      <tr key={item.service_id || index} className="hover:bg-blue-50 transition">
                        <td className="px-2 border py-1 text-center text-sm text-gray-600">{srn}</td>
                        <td className="px-2 border py-1 text-sm font-medium text-gray-800">{item.service_name || 'N/A'}</td>
                        <td className="px-2 border py-1 text-sm text-gray-600">{item.vendor_name || 'N/A'}</td>
                        <td className="px-2 border py-1 text-xs font-mono text-gray-600">{item.end_point || 'N/A'}</td>
                        <td className="px-2 border py-1 text-center text-sm font-semibold text-gray-800">
                          {parseInt(item.total_requests || 0).toLocaleString()}
                        </td>
                        <td className="px-2 py-1 border text-center text-sm font-semibold text-green-600">
                          {parseInt(item.success_responses || 0).toLocaleString()}
                        </td>
                        <td className="px-2 py-1 border text-center text-sm font-semibold text-blue-600">
                          {parseInt(item.cache_responses || 0).toLocaleString()}
                        </td>
                        <td className="px-2 py-1 border text-center text-sm font-semibold text-red-600">
                          {parseInt(item.failed_responses || 0).toLocaleString()}
                        </td>
                        <td className="px-2 py-1 border text-center text-sm font-semibold text-purple-600">
                          {parseInt(item.vendor_request || 0).toLocaleString()}
                        </td>
                        <td className={`px-2 py-1 border text-center text-sm font-bold ${
                          parseFloat(successRate) >= 95 ? 'text-green-600' : 
                          parseFloat(successRate) >= 80 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {item.success_percentage}%
                        </td>
                        <td className="px-2 py-1 border text-right text-sm font-semibold text-gray-800">
                          ₹{parseFloat(item.total_amount || 0).toFixed(2)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col md:flex-row justify-between items-center mt-6 gap-4">
              <p className="text-sm text-gray-600">
                Showing page <span className="font-semibold">{currentPage}</span> of <span className="font-semibold">{totalPages}</span>
                {' '}(<span className="font-semibold">{totalRecords.toLocaleString()}</span> total records)
              </p>
              
              <nav>
                <ul className="flex items-center gap-2">
                  <li>
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1 || loading}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      First
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || loading}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      Previous
                    </button>
                  </li>
                  
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    if (pageNum < 1 || pageNum > totalPages) return null;
                    
                    return (
                      <li key={pageNum}>
                        <button
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                          className={`px-3 py-2 text-sm border rounded-lg transition ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white border-blue-600 font-semibold'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  
                  <li>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || loading}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      Next
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages || loading}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      Last
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ServiceUsageReport;
